import EstruturaController from '@/assets/ts/controller/EstruturaController';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { State, Action, Getter, Mutation } from 'vuex-class';
import 'vue-organization-chart/dist/orgchart.css';
import { forEach, now } from 'lodash';
import ChartNode from '@/../bower_components/teknisavuecomponents/assets/interfaces/ChartNode';
import TkOrganogram from '@/../bower_components/teknisavuecomponents/components/chart/TkOrganogram/TkOrganogram.vue';
import EstruturaPopup from '@/components/hierarquia/EstruturaPopup/EstruturaPopup.vue';
import VueUtils from '@/../bower_components/teknisavuecomponents/assets/utils/VueUtils';
import Util from '@/../bower_components/teknisavuecomponents/assets/utils/Util';

@Component({
  name: 'HierarquiaOrganizacao',
  components: {
    TkOrganogram,
    EstruturaPopup,
  },
})
class HierarquiaOrganizacao extends Vue {
  // #region [ STORE ]
  @Mutation('changeCurrentView')
  private changeCurrentView: (string) => void;
  // #endregion

  // #region [ DATA ]
  private readonly viewTitle = 'Organograma da Organização';

  private loading = false;

  private dragOptions = {
    eventsAreCreated: false,
  };

  private chartDatasource: ChartNode = null;

  private chartConfig = {
    pan: false,
    zoom: false,
  };

  private zoomOptions = {
    scale: 1,
    minScale: 0.1,
    maxScale: 2,
    zoom: 1.2,
  }

  private estruturaPopup = {
    show: false,
    row: {} as any,
  };
  // #endregion

  // #region [ COMPUTED ]
  // #endregion

  // #region [ LIFECYCLE ]
  private created() {
    this.changeCurrentView(this.viewTitle);
    (window as any).hierarquiaOrganizacao = this;

    this.reload();
  }
  // #endregion

  // #region [ WATCHERS ]
  // #endregion

  // #region [ METHODS ]
  private async reload(): Promise<void> {
    this.loading = true;

    try {
      const hierarquia = await EstruturaController.getHierarquiaOrganizacao();
      this.chartDatasource = this.estruturaToChartNode(hierarquia);

      await Util.setTimeout(1000);
      this.addChartEventListeners();
    } catch (err) {
      const message = 'Não foi possível carregar a estrutura da organização!';
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);

      this.chartDatasource = null;

      throw err;
    } finally {
      this.loading = false;
    }
  }

  private estruturaToChartNode(
    estrutura: any,
  ): ChartNode {
    const nmResponsavel = estrutura.NMPARCRESPINF;

    const title = nmResponsavel ?
      `${estrutura.NMTIPOESTRUTURA} - ${nmResponsavel}` :
      estrutura.NMTIPOESTRUTURA;

    const children = estrutura.__children.map((child: any) => {
      return this.estruturaToChartNode(child);
    });

    const node: ChartNode = {
      id: estrutura.NRESTRUTURAM,
      name: estrutura.NMESTRUTURAH,
      title,
      children,
      __row: estrutura,
    };

    return node;
  }

  private chartOnDoubleClick(node: ChartNode): void {
    this.openEstruturaPopup(node);
  }

  private openEstruturaPopup(node: ChartNode): void {
    this.estruturaPopup.show = true;
    this.estruturaPopup.row = node.__row;
  }

  private zoomIn() {
    this.zoom(true);
  }

  private zoomOut() {
    this.zoom(false);
  }

  private zoom(
    zoomIn = true,
    amount = this.zoomOptions.zoom,
    event?: WheelEvent,
  ) {
    const newScale = zoomIn ?
      this.zoomOptions.scale * amount :
      this.zoomOptions.scale / amount;

    if (newScale < this.zoomOptions.minScale || newScale > this.zoomOptions.maxScale) {
      return;
    }

    this.zoomOptions.scale = newScale;

    this.updateChartScale();
    this.updateChartScroll(zoomIn, amount, event);
  }

  private updateChartScale(): void {
    const orgChartsGroup = document.getElementsByClassName('orgchart');
    const orgChart = orgChartsGroup[0];

    if (!orgChart) {
      return;
    }

    const orgChartTable = orgChart.children[0] as HTMLElement;

    if (!orgChartTable) {
      return;
    }

    orgChartTable.style.transform = `scale(${this.zoomOptions.scale})`;
  }

  private updateChartScroll(
    zoomIn = true,
    amount = this.zoomOptions.zoom,
    event?: WheelEvent,
  ) {
    const orgChartContainer = this.getOrgChartContainer();

    if (!orgChartContainer) {
      return;
    }

    const distanceToCenterX = event ? event.pageX : orgChartContainer.clientWidth / 2;
    const distanceToCenterY = event ? event.pageY : orgChartContainer.clientHeight / 2;

    const centerX = orgChartContainer.scrollLeft + distanceToCenterX;
    const centerY = orgChartContainer.scrollTop + distanceToCenterY;

    const newCenterX = zoomIn ? (centerX * amount) : (centerX / amount);
    const newCenterY = zoomIn ? (centerY * amount) : (centerY / amount);

    const scrollX = newCenterX - distanceToCenterX;
    const scrollY = newCenterY - distanceToCenterY;

    orgChartContainer.scrollTo(scrollX, scrollY);
  }

  private addChartEventListeners(): void {
    if (this.dragOptions.eventsAreCreated) {
      return;
    }

    const breakpoint = VueUtils.getBreakpoint();
    const isMobile = breakpoint === 'xs';

    if (isMobile) {
      return;
    }

    const orgChartContainer = this.getOrgChartContainer();

    if (!orgChartContainer) {
      return;
    }

    orgChartContainer.addEventListener('mousemove', this.chartOnMouseMove);
    orgChartContainer.addEventListener('wheel', this.chartOnWheel, {passive: false});

    this.dragOptions.eventsAreCreated = true;
  }

  private chartOnMouseMove(ev: MouseEvent): void {
    const isMouseDown = ev.buttons === 1;

    if (!isMouseDown) {
      return;
    }

    const orgChartContainer = this.getOrgChartContainer();

    if (!orgChartContainer) {
      return;
    }

    const scrollByX = ev.movementX * -1;
    const scrollByY = ev.movementY * -1;

    orgChartContainer.scrollBy(scrollByX, scrollByY);
  }

  private chartOnWheel(event: WheelEvent): void {
    if (!event.ctrlKey) {
      return;
    }

    const zoomIn = event.deltaY < 0;
    const zoomAmount = Math.max(Math.abs(event.deltaY * 0.0085), 1.02);

    this.zoom(zoomIn, zoomAmount, event);
    event.preventDefault();
  }

  private getOrgChartContainer(): HTMLElement {
    const orgChartContainersGroup = document.getElementsByClassName('orgchart-container');
    const orgChartContainer = orgChartContainersGroup[0] as HTMLElement;

    return orgChartContainer || null;
  }
  // #endregion
}

const self = HierarquiaOrganizacao;

export default HierarquiaOrganizacao;
